import {WIDTH, HEIGHT} from 'components/effects/plane/constants.js';
import {randomize} from './randomize.js';
import {setEffectParameterValues} from 'actions/index.js';
import {shuffle} from "utils/ShuffleArray.js"

const NUM_VARIATIONS = 16;
const NUM_MUTATIONS = 3;

export default superclass => class extends superclass  {
    variations = [];
    generation = 0;
    _bestSolutions = [];

    firstReproduction() {
        let coords = [];                
        this._parameters.forEach(
            p => coords[p.id] = p.getCoordinates());
        this.variations = this.generateVariations(coords);
    }
    
    getBestCopy() {
        return this.bestSolutions.map(
                v => ({... v}));
    }

    reproduce(degree=13.0/16.0) {
        if (this.generation === 0) {
            this.firstReproduction();
        } else {
            let bestSolutions = this.getBestCopy();
            if (bestSolutions.length === 0) {
                return;
            }
            let newSolutions = [... bestSolutions]; // copy over best solutions
            let nonBestSolutions = [... this.nonBestSolutions];
            let children = [];
            for (let i=newSolutions.length; i < nonBestSolutions.length - NUM_MUTATIONS; i++) {
                // breed each "non-best" solution with a best solution
                let eliteSolution = randChoose(bestSolutions);
                let nonEliteSolution = randChoose(nonBestSolutions);
                let bredSolution = this.breed(eliteSolution, nonEliteSolution);
                children.push(bredSolution);
                newSolutions.push(bredSolution);
            }
            let mutations = [];
            for (let i=0; i < NUM_MUTATIONS; i++) {
                // randomly generate some mutations based off the best solution
                let goodSolution = randChoose(bestSolutions);
                let mutation = this.generateVariation(goodSolution, degree);
                mutations.push(mutation);
                newSolutions.push(mutation);
            }
            this.categories = {
                elite: bestSolutions,
                children,
                mutations
            };
            this.variations = newSolutions;
        }

        this.generation++;
        this._bestSolutions = [];
    }

    selectBest(num) {
        if (this._bestSolutions.includes(num)) {
            // then we need to remove that
            this._bestSolutions = this._bestSolutions.filter(
                x => x !== num);
        } else {
            // we just add it
            this._bestSolutions.push(num);
        }
    }

    selectVariation(num, time, rampTime) {
        let variation = this.variations[num];
        let bulk = {};
        for (let _id in variation) {
            let param = this._parameters.find(p => p.id === _id);
            if (!param) {
                continue;
            }
            let {x,y} = variation[_id];
            let {id, value} = param.setCoordinates(x, y, time, rampTime, true);
            this.effect.clearParameter(param, undefined, true);
            bulk[id] = value;
        }
        this.track.sequencerEngine.dispatch(
            setEffectParameterValues(bulk));
        this.selectedVariation = num;
    }

    // breeds 2 solutions together
    /**
     * a: elite
     * b : non-elite
     */
    breed(a, b) {
        let solution = {};
        for (let {id} of this._parameters) {
            let flip = Math.random();
            // bias towards elite
            if (flip < 0.6) {
                solution[id] = {... a[id]};
            } else {
                solution[id] = {... b[id]};
            }
        }
        return solution;
    }

    get bestSolutions() {
        return this._bestSolutions.map(
            x => this.variations[x]);
    }

    get nonBestSolutions() {
        let solutions = [];
        for (let i in this.variations) {
            if (!this.bestSolutions.includes(i)) {
                solutions.push(this.variations[i]);
            }
        }
        return solutions;
    }

    // initializes variations based on the current group
    generateVariations(coordinates) {
        let degree = 1.0/8.0;
        // always have the "starting point" as the initial variation
        let variations = [coordinates];
        for (let i=1; i < NUM_VARIATIONS; i++) {
            let variation = this.generateVariation(coordinates, degree);
            variations.push(variation);
            degree += 1.0/32.0;
        }
        return variations;
    }

    generateVariation(coordinates, degree) {
        let variation = {};
        for (let p of this._parameters) {
            if (!coordinates[p.id]) {
                continue;
            }
            let {x,y} = coordinates[p.id];
            variation[p.id] = {
                x: randomize(x, 0, WIDTH, degree),
                y: randomize(y, 0, HEIGHT, degree)
            };
        }
        return variation;
    };

}

const randChoose = (x) => x[Math.floor(Math.random() * x.length)];
